import { EventEmitter } from '@alphahub/eie-core';
import { WebAudioCapture } from '../WebAudioCapture';
import type { EmotionAPIClient } from '@alphahub/eie-core';

type Getters = {
  isRunning: () => boolean;
  getRecentStates: (windowMs: number) => any[];
  getRecentPoseSnapshots: (windowMs: number) => any[];
  getLastValidFaceDetectionTime: () => number;
  getLatestHeartRate: () => any | undefined;
  getAbortSignal: () => AbortSignal | undefined;
  getSessionId: () => string;
  getBaseline: () => any | undefined;
};

type Setters = {
  incrementPendingRequests: () => void;
  decrementPendingRequests: () => void;
  pushDiagnosis: (d: any) => void;
  emit: (e: string, p: any) => void;
  pushTimelineUpdate: (s: any) => void;
  getPendingMilestoneChunkId: () => number | undefined;
  setPendingMilestoneChunkId: (id?: number) => void;
};

export interface IAudioMilestoneManager {
  start: () => Promise<void>;
  stop: () => void;
  setDryRun: (v: boolean) => void;
}

/**
 * AudioMilestoneManager handles WebAudioCapture lifecycle and milestone/chunk processing
 * by delegating to the provided getters/setters and using the provided `apiClient`.
 */
export class AudioMilestoneManager implements IAudioMilestoneManager {
  private dryRun = false;
  private audioCapture?: WebAudioCapture;

  constructor(
    private config: any,
    private emitter: EventEmitter,
    private apiClient: EmotionAPIClient,
    private getters: Getters,
    private setters: Setters,
  ) {}

  setDryRun(v: boolean): void { this.dryRun = !!v; }

  async start(): Promise<void> {
    // create capture if not present
    if (this.audioCapture) return;
    this.audioCapture = new WebAudioCapture({
      chunkDuration: this.config.intervals.emotion,
      milestoneDuration: this.config.intervals.milestone,
      chunkBuffer: this.config.intervals.chunkBuffer,
    });

    this.audioCapture.onChunkReady((chunk) => this.handleChunk(chunk));
    await this.audioCapture.start();
  }

  stop(): void {
    if (this.audioCapture) { try { this.audioCapture.stop(); } catch {} this.audioCapture = undefined; }
  }

  private async handleChunk(chunk: { base64Audio: string | null; timestamp: number; chunkId: number; isMilestone: boolean; }): Promise<void> {
    if (!this.getters.isRunning()) return;

    // At milestone boundaries, both chunk and milestone fire with same chunkId.
    if (chunk.isMilestone) {
      this.setters.setPendingMilestoneChunkId(chunk.chunkId);
    } else if (this.setters.getPendingMilestoneChunkId() === chunk.chunkId) {
      this.setters.setPendingMilestoneChunkId(undefined);
      return;
    }

    if (!chunk.isMilestone && this.config.intervals.chunkBuffer === 0) return;

    const windowDuration = chunk.isMilestone ? 30000 : this.config.intervals.chunkBuffer * 5000;
    const recentStates = this.getters.getRecentStates(windowDuration + 1000);
    const recentPoseSnapshots = this.getters.getRecentPoseSnapshots(windowDuration);

    if (recentStates.length === 0) return;

    if (this.dryRun) {
      console.log(`🧪 DRY RUN chunk ${chunk.chunkId}: ${recentStates.length} states, audio: ${chunk.base64Audio ? 'YES' : 'NO'}, milestone: ${chunk.isMilestone}`);
      return;
    }

    const currentState = recentStates[recentStates.length - 1];

    try {
      this.setters.incrementPendingRequests();
      let diagnosis: any = null;
      try {
        const now2 = Date.now();
        const faceDetectionValid = (now2 - this.getters.getLastValidFaceDetectionTime()) < 500;

        diagnosis = await this.apiClient.analyzeMilestone({
          sessionId: this.getters.getSessionId(),
          states: recentStates,
          baseline: this.getters.getBaseline(),
          audioBase64: chunk.base64Audio,
          bodyPoseSnapshots: recentPoseSnapshots.length > 0 ? recentPoseSnapshots : undefined,
          heartRate: this.getters.getLatestHeartRate(),
          chunkIndex: chunk.chunkId,
          isMilestone: chunk.isMilestone,
          windowSeconds: chunk.isMilestone ? this.config.intervals.milestone / 1000 : (this.config.intervals.chunkBuffer * this.config.intervals.emotion / 1000),
          snapshotCount: chunk.isMilestone ? this.config.intervals.milestone / this.config.intervals.emotion : this.config.intervals.chunkBuffer,
          isFaceDetectionValid: faceDetectionValid,
          signal: this.getters.getAbortSignal(),
        });
      } finally {
        this.setters.decrementPendingRequests();
      }

      if (diagnosis) {
        this.setters.pushDiagnosis(diagnosis);
        this.emitter.emit('diagnosis', diagnosis);
        if (chunk.isMilestone) {
          this.emitter.emit('milestone', { diagnosis, states: recentStates });
        }
      }

      const stateWithBackendData = {
        ...currentState,
        chunkIndex: chunk.chunkId,
        isMilestone: chunk.isMilestone,
        emotion: diagnosis?.emotion || currentState.emotion,
        valence: diagnosis?.valence ?? currentState.valence,
        arousal: diagnosis?.arousal ?? currentState.arousal,
        confidence: diagnosis?.confidence ?? currentState.confidence,
        context: (diagnosis as any)?.context,
        insight: (diagnosis as any)?.insight,
        recommendation: (diagnosis as any)?.recommendation,
        riskLevel: diagnosis?.riskLevel,
        emotionalPattern: (diagnosis as any)?.emotionalPattern,
        transcript: diagnosis?.transcript,
        reasoning: (diagnosis as any)?.reasoning,
        baseline: diagnosis?.baseline || undefined,
        baselineDeviation: undefined,
      };
      this.emitter.emit('emotionUpdate', stateWithBackendData);
    } catch (error) {
      console.error(`❌ Chunk ${chunk.chunkId} analysis failed:`, error);
    }
  }
}
